import type { JSONContent } from "@tiptap/core";
import {
  entityRelationshipBuiltInLabels,
  entityRelationshipLabelLabels,
  summarizeEntityCard,
  type EntityCardView,
  type EntityRelationshipRecord,
} from "@/features/entities";
import type { LoreEntryView } from "@/types/lore";
import { buildAffinityChartEndpointNodeId, sanitizeAffinityChartPositions } from "@/features/affinity-chart/affinity-chart-elements";
import {
  createAffinityChartNodeId,
  getAffinityChartCanonStatusLabelKey,
  getAffinityChartNodeHref,
  getAffinityChartRelationTypeLabelKey,
  getAffinityChartTypeLabelKey,
  type AffinityChartFilterOption,
  type AffinityChartSnapshot,
} from "@/features/affinity-chart/affinity-chart.types";

type BuildAffinityChartSnapshotInput = {
  entityCards: EntityCardView[];
  generatedAt?: number;
  layoutPositions: unknown;
  loreEntries: LoreEntryView[];
  projectId: string;
  relationships: EntityRelationshipRecord[];
};

function extractTextContent(node: JSONContent | null | undefined): string {
  if (!node) {
    return "";
  }

  if (typeof node.text === "string") {
    return node.text;
  }

  return (node.content ?? []).map((child) => extractTextContent(child)).join(" ");
}

function summarizeLoreEntry(entry: LoreEntryView) {
  return extractTextContent(entry.mainBody).replace(/\s+/g, " ").trim();
}

function formatRelationshipLabel(label: string) {
  return (entityRelationshipBuiltInLabels as string[]).includes(label)
    ? entityRelationshipLabelLabels[label as keyof typeof entityRelationshipLabelLabels]
    : label;
}

export function buildAffinityChartSnapshot({
  entityCards,
  generatedAt = Date.now(),
  layoutPositions,
  loreEntries,
  projectId,
  relationships,
}: BuildAffinityChartSnapshotInput) {
  const nodes = [
    ...entityCards.map((card) => ({
      canonStatus: null,
      canonStatusLabelKey: null,
      family: "entity-card" as const,
      href: getAffinityChartNodeHref(projectId, "entity-card", card.id),
      id: createAffinityChartNodeId("entity-card", card.id),
      imageUrl: card.imageUrl,
      label: card.name,
      recordId: card.id,
      recordKind: "entity-card" as const,
      summaryText: summarizeEntityCard(card),
      type: card.type,
      typeLabelKey: getAffinityChartTypeLabelKey(card.type),
    })),
    ...loreEntries.map((entry) => ({
      canonStatus: entry.canonStatus,
      canonStatusLabelKey: getAffinityChartCanonStatusLabelKey(entry.canonStatus),
      family: "lore-entry" as const,
      href: getAffinityChartNodeHref(projectId, "lore-entry", entry.id),
      id: createAffinityChartNodeId("lore-entry", entry.id),
      imageUrl: entry.coverImageUrl,
      label: entry.title,
      recordId: entry.id,
      recordKind: "lore-entry" as const,
      summaryText: summarizeLoreEntry(entry),
      type: entry.type,
      typeLabelKey: getAffinityChartTypeLabelKey(entry.type),
    })),
  ].sort((left, right) => left.label.localeCompare(right.label));

  const nodesById = new Map(nodes.map((node) => [node.id, node]));

  const edges = relationships.flatMap((relationship) => {
    const sourceNodeId = buildAffinityChartEndpointNodeId(relationship.from_kind, relationship.from_id);
    const targetNodeId = buildAffinityChartEndpointNodeId(relationship.to_kind, relationship.to_id);
    const sourceNode = nodesById.get(sourceNodeId);
    const targetNode = nodesById.get(targetNodeId);

    if (!sourceNode || !targetNode) {
      return [];
    }

    return [
      {
        id: relationship.id,
        label: formatRelationshipLabel(relationship.label),
        relationType: relationship.rel_type,
        relationTypeLabelKey: getAffinityChartRelationTypeLabelKey(relationship.rel_type),
        sourceLabel: sourceNode.label,
        sourceNodeId,
        targetLabel: targetNode.label,
        targetNodeId,
      },
    ];
  });

  const filterOptions = nodes.reduce<Map<string, AffinityChartFilterOption>>((options, node) => {
    const current = options.get(node.type);

    if (current) {
      current.count += 1;
      return options;
    }

    options.set(node.type, {
      count: 1,
      family: node.family,
      labelKey: node.typeLabelKey,
      type: node.type,
    });

    return options;
  }, new Map());

  return {
    counts: {
      edges: edges.length,
      entityNodes: entityCards.length,
      loreNodes: loreEntries.length,
    },
    edges,
    filterOptions: Array.from(filterOptions.values()).sort((left, right) =>
      left.labelKey.localeCompare(right.labelKey),
    ),
    generatedAt,
    layoutPositions: sanitizeAffinityChartPositions(
      layoutPositions,
      new Set(nodes.map((node) => node.id)),
    ),
    nodes,
    projectId,
  } satisfies AffinityChartSnapshot;
}
